<template>
  <section id="calculator-section" class="section">
   <section-content-wrapper noPaddingBottom>
    <div class="calculator-section-wrapper">
      <section-title :text="title" />
      <div id="calculator">
        <section-sub-title :text="'Określ kwotę finansowania'"
        id="calculator-input-label"
        />
        <div class="calculator-actions">
          <div class="input-container">
            <input
            id="calculator-input"
            type="number"
            :min="min"
            :max="max"
            :step="step"
            v-model="calculatorValue"
            @input="validateInputSetValue($event.target.value)"
            @change="validateInputChangeValue($event.target.value)"
            class="calculator-input"
            aria-labelledby="calculator-input-label"
            aria-describedby="calculator-range-info calculator-results"
            >
          </div>
          <div class="slider-container" aria-hidden="true">
            <input
            tabindex="-1"
            id="calculator-slider"
            type="range"
            ref="slider"
            :style="{ '--rangeWidth': setRange() }"
            :min="min"
            :max="max" :step="step"
            v-model="calculatorValue"
            class="calculator-slider"
            aria-labelledby="calculator-input-label"
            aria-activedescendant="calculator-results"
           >
            <div class="slider-text" id="calculator-range-info">
              <span class="calculator-info-text"><span class="visually-hidden">Od</span>{{setFormattedMinValue()}}</span>
              <span class="calculator-info-text"><span class="visually-hidden">Do</span>{{setFormattedMaxValue()}}</span>
            </div>
          </div>
        </div>
        <div class="table-container" role="region" id="calculator-results">
          <h4 id="calculator-results-heading" class="visually-hidden">Wyniki symulacji finansowania</h4>
          <ul aria-labelledby="calculator-results-heading">
            <li>
              <span>Otrzymujesz na konto</span>
              <span class="calculator-text-box">
                <span id="calculator-amount">
                {{setCalculateAmount()}}
                </span>
              </span>
            </li>
            <li>
              <span>Łączny koszt</span>
              <span class="calculator-text-box">
                <span id="calculator-cost">{{setCalculateCost()}}</span>
              </span>
            </li>
            <li>
              <span>Do spłaty</span>
              <span class="calculator-text-box">
                <span id="calculator-sum">{{setCalculateSum()}}</span>
              </span>
            </li>
          </ul>
          <ul aria-labelledby="calculator-results-heading">
            <li>
              <span>Spłata dzienna</span>
              <span class="calculator-text-box">
                <span class="visually-hidden">
                  x% - procent poznasz po sprawdzeniu limitu
                </span>
                <span aria-hidden="true">X%</span>
              </span>
            </li>
            <li>
              <span>Przewidywana spłata</span>
              <span class="calculator-text-box">
                <time id="calculator-date">{{ setRepaymentDateShorter() }}</time></span>
            </li>
            <li>
              <span>Maks. czas spłaty</span>
              <span class="calculator-text-box">
                <time id="calculator-max-date">{{setRepaymentDateMax()}}</time></span>
            </li>
          </ul>
        </div>
      </div>
    </div>
   <div class="calculator-caption-wrapper">
    <p class="calculator-caption">
      Powyższe wartości procentowe są przykładowe – dokładną wartość poznasz po indywidualnej analizie.<br />Prognozowana spłata zakłada domyślnie 12-miesięczny okres, z możliwością otrzymania dodatkowego miesiąca.
    </p>
    <div class="calculator-buttons-wrapper">
      <button-open-dialog></button-open-dialog>
      <a class="button secondary" href="#scenario-section">Zobacz przykłady finansowania</a>
    </div>
   </div>
   </section-content-wrapper>
  </section>
</template>

<script>
import SectionTitle from '../common/SectionTitle'
import SectionSubTitle from '../common/SectionSubTitle'
import SectionContentWrapper from '../common/SectionContentWrapper'
import ButtonOpenDialog from '../common/ButtonOpenDialog.vue'
import { CostCalculatorClass, NumberFormatterClass } from '../../class/calculator.class'
import { DateClass } from '../../class/date.class'

export default {
  name: 'CalculatorSection',
  components: {
    SectionTitle,
    SectionSubTitle,
    SectionContentWrapper,
    ButtonOpenDialog
  },
  props: ['content'],
  data () {
    return {
      slider: null,
      thumb: this.content.thumbWidth,
      title: this.content.title,
      currency: this.content.currency,
      min: this.content.amount.min,
      max: this.content.amount.max,
      step: this.content.amount.step,
      calculatorValue: this.content.amount.start,
      calculatorPercent: this.content.amount.percent,
      repaymentTerm: this.content.dates.startRepaymentTerm,
      repaymentMaxTerm: this.content.dates.startRepaymentMaxTerm
    }
  },
  methods: {
    setFormattedMaxValue () {
      return NumberFormatterClass.formatAmount(this.max)
    },
    setFormattedMinValue () {
      return NumberFormatterClass.formatAmount(this.min)
    },
    validateInputSetValue (value) {
      if (value > this.max) {
        this.calculatorValue = this.max
      }
    },
    validateInputChangeValue (value) {
      if (value < this.min) {
        this.calculatorValue = this.min
      }
      if (value > this.max) {
        this.calculatorValue = this.max
      }
    },
    setCalculateAmount () {
      if (this.calculatorValue >= this.min) {
        return NumberFormatterClass.formatAmount(this.calculatorValue)
      }
      if (this.calculatorValue < this.min) {
        return 0
      }
    },
    setCalculateCost () {
      if (this.calculatorValue >= this.min) {
        const cost = new CostCalculatorClass(this.calculatorValue, this.calculatorPercent)
        return NumberFormatterClass.formatAmount(cost.cost)
      }
      if (this.calculatorValue < this.min) {
        return 0
      }
    },
    setCalculateSum () {
      if (this.calculatorValue >= this.min) {
        const cost = new CostCalculatorClass(this.calculatorValue, this.calculatorPercent)
        return NumberFormatterClass.formatAmount(cost.sum)
      }
      if (this.calculatorValue < this.min) {
        return 0
      }
    },
    setRange () {
      if (this.slider) {
        const position = (this.calculatorValue - this.min) / (this.max - this.min)
        const thumbCorrect = this.thumb * (position - 0.5) * -1
        const sliderWidth = this.$refs.slider.offsetWidth
        const thumbPosition = Math.round((position * sliderWidth) - this.thumb / 3 + thumbCorrect)
        return thumbPosition + 'px'
      }
    },
    setRepaymentDateShorter () {
      return DateClass.setYear(1)
    },
    setRepaymentDateMax () {
      return DateClass.setMaxDate(1, this.repaymentMaxTerm)
    },
    onResize () {
      this.slider = this.$refs.slider.offsetWidth
      this.setRange()
    }
  },
  mounted () {
    this.slider = this.$refs.slider.offsetWidth
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.onResize)
  }
}
</script>
